import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import WhatsAppButton from '../components/WhatsAppButton';
import HeroSection from '../components/HeroSection';
import BenefitCard from '../components/BenefitCard';
import CaseCard from '../components/CaseCard';
import TestimonialCard from '../components/TestimonialCard';
import FAQAccordion from '../components/FAQAccordion';
import DemoForm from '../components/DemoForm';
import { UserCheck, Clock, Database, X } from 'lucide-react';

const AIPage = () => {
  const { country } = useParams();
  const [showDemoForm, setShowDemoForm] = useState(false);

  const benefits = [
    {
      icon: UserCheck,
      title: 'Qualificação Automática de Leads',
      description: 'Agentes de IA analisam cada lead e os qualificam automaticamente. Sua equipe de vendas recebe apenas leads qualificados, prontos para conversão.'
    },
    {
      icon: Clock,
      title: 'Atendimento 24/7 Inteligente',
      description: 'Seus clientes são atendidos instantaneamente, a qualquer hora. Os agentes respondem perguntas, resolvem problemas e encaminham para humanos quando necessário.'
    },
    {
      icon: Database,
      title: 'Processamento de Dados em Escala',
      description: 'Processe milhões de dados em segundos. Agentes de IA identificam padrões, geram insights e automatizam decisões baseadas em dados.'
    }
  ];

  const cases = [
    {
      title: 'Redução de 70% no Tempo de Resposta',
      company: 'E-commerce Tech',
      challenge: 'Equipe pequena, volume grande de mensagens de clientes. Tempo de resposta longo afetava satisfação.',
      solution: 'Implementamos Agente de IA para atender primeiras demandas e qualificar para atendimento humano.',
      result: '70% redução no tempo de resposta | 40% redução em custo'
    },
    {
      title: 'Qualificação de Leads Aumentou 300%',
      company: 'B2B Solutions',
      challenge: 'Equipe de vendas gastava horas qualificando leads manualmente.',
      solution: 'Agente de IA customizado para qualificar leads conforme critérios específicos.',
      result: '300% aumento em leads qualificados | 25% aumento em conversão'
    }
  ];

  const testimonials = [
    {
      name: 'Carlos Mendes',
      position: 'CEO',
      company: 'Startup Innovate',
      testimonial: 'Os Agentes de IA revolucionaram nossa operação. Agora temos atendimento 24/7 sem aumentar a equipe. O suporte da Agro Urban Global foi excelente!'
    },
    {
      name: 'Ana Costa',
      position: 'Gerente de Marketing',
      company: 'Digital First',
      testimonial: 'Implementar os Agentes de IA foi a melhor decisão. Qualificação automática, atendimento inteligente, e ainda economizamos em custos!'
    }
  ];

  const faqs = [
    {
      question: 'Como os Agentes de IA funcionam?',
      answer: 'Os Agentes de IA são programados com regras e treinamento específico para sua empresa. Eles analisam dados, reconhecem padrões e tomam ações automaticamente.'
    },
    {
      question: 'Os Agentes de IA podem trabalhar com meu CRM?',
      answer: 'Sim! Os Agentes de IA desenvolvidos com N8N integram perfeitamente com CRM Kommo e centenas de outras ferramentas.'
    },
    {
      question: 'Quanto tempo leva para implementar?',
      answer: 'Depende da complexidade, mas geralmente entre 2 a 6 semanas. Oferecemos suporte completo durante a implementação.'
    },
    {
      question: 'Qual é o ROI esperado?',
      answer: 'Clientes típicos veem ROI em 2-3 meses através de redução de custos operacionais, aumento de produtividade e melhoria em conversão.'
    }
  ];

  return (
    <div className="min-h-screen bg-aug-cream">
      <Header />
      <WhatsAppButton />
      
      <div className="pt-20">
        <HeroSection
          title="Agentes de Inteligência Artificial - Automação 24/7"
          subtitle="Trabalhe enquanto seus agentes de IA fazem o trabalho pesado"
          description="Agentes de IA que trabalham 24/7 para qualificar leads, atender clientes, processar dados e otimizar seus processos."
          ctaText="Solicitar Demonstração"
          ctaAction={() => setShowDemoForm(true)}
        />

        <section className="py-4xl bg-white">
          <div className="max-w-[1400px] mx-auto px-xl">
            <h2 className="text-4xl font-bold text-aug-gray-dark mb-3xl text-center">Benefícios</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {benefits.map((benefit, index) => (
                <BenefitCard key={index} {...benefit} />
              ))}
            </div>
          </div>
        </section>

        <section className="py-4xl bg-aug-cream">
          <div className="max-w-[1400px] mx-auto px-xl">
            <h2 className="text-4xl font-bold text-aug-gray-dark mb-3xl text-center">Casos de Sucesso</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {cases.map((caseItem, index) => (
                <CaseCard key={index} {...caseItem} />
              ))}
            </div>
          </div>
        </section>

        <section className="py-4xl bg-white">
          <div className="max-w-[1400px] mx-auto px-xl">
            <h2 className="text-4xl font-bold text-aug-gray-dark mb-3xl text-center">Depoimentos</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {testimonials.map((testimonial, index) => (
                <TestimonialCard key={index} {...testimonial} />
              ))}
            </div>
          </div>
        </section>

        <section className="py-4xl bg-aug-cream">
          <div className="max-w-[1400px] mx-auto px-xl">
            <h2 className="text-4xl font-bold text-aug-gray-dark mb-3xl text-center">Perguntas Frequentes</h2>
            <div className="max-w-3xl mx-auto">
              <FAQAccordion faqs={faqs} />
            </div>
          </div>
        </section>

        <section className="py-4xl bg-gradient-to-r from-aug-blue to-aug-green text-white">
          <div className="max-w-[1400px] mx-auto px-xl text-center">
            <h2 className="text-4xl font-bold mb-4">Pronto para automatizar com IA?</h2>
            <p className="text-xl mb-8">Descubra como nossos Agentes de IA podem transformar seu negócio</p>
            <button
              onClick={() => setShowDemoForm(true)}
              className="bg-white text-aug-blue px-8 py-4 rounded-xl font-semibold text-lg hover:bg-aug-cream transition-all duration-300"
              data-testid="ai-cta-button"
            >
              Comece Agora
            </button>
          </div>
        </section>
      </div>

      {showDemoForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto p-6 relative">
            <button
              onClick={() => setShowDemoForm(false)}
              className="absolute top-4 right-4 text-aug-gray-dark hover:text-aug-error"
            >
              <X className="w-6 h-6" />
            </button>
            <h3 className="text-2xl font-bold text-aug-gray-dark mb-6">Solicitar Demonstração</h3>
            <DemoForm country={country} onClose={() => setShowDemoForm(false)} />
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default AIPage;